import type { EntrySkeletonType } from 'contentful';

/**
 * Configuration type for brand name processing
 * Can be a boolean for processing all fields, or an object specifying fields per content type
 *
 * Example usage:
 * ```typescript
 * // Process all fields in all content types
 * const config: BrandNameProcessingConfig = true;
 *
 * // Process specific content types
 * const config: BrandNameProcessingConfig = {
 *   blogPage: true, // Process all fields in blogPage
 *   contentCard: ['title', 'text'] // Process only specific fields in contentCard
 * };
 *
 * // With type checking for a single content type
 * import { TypeBlogPageSkeleton } from '@tuic/contentful/generated/website';
 *
 * const config: ContentTypeConfig<TypeBlogPageSkeleton> = {
 *   blogPage: ['title', 'teaserText'],   // Field intellisense for blogPage
 *   contentCard: ['text'],               // Other content types allowed
 * };
 *
 * // With type checking for multiple content types
 * import {
 *   TypeBlogPageSkeleton,
 *   TypeContentCardSkeleton,
 *   TypeTextSectionSkeleton
 * } from '@tuic/contentful/generated/website';
 *
 * const config: ContentTypeConfig<[
 *   TypeBlogPageSkeleton,
 *   TypeContentCardSkeleton,
 *   TypeTextSectionSkeleton
 * ]> = {
 *   blogPage: ['title', 'teaserText'],    // Field intellisense
 *   contentCard: ['title', 'text'],       // Field intellisense
 *   textSection: ['heading', 'body'],     // Field intellisense
 *   otherType: ['anyField']               // Other types allowed
 * };
 * ```
 */
export type BrandNameProcessingConfig =
  | boolean
  | Record<string, boolean | string[]>;

/**
 * Utility to extract the content type ID from a skeleton type
 */
type ExtractContentTypeId<T extends EntrySkeletonType> =
  T extends EntrySkeletonType<any, infer ID> ? ID : never;

/**
 * Configuration for a single content type with field intellisense
 */
type SingleContentTypeConfig<T extends EntrySkeletonType> = {
  [K in ExtractContentTypeId<T>]?: T extends EntrySkeletonType<infer F, any>
    ? boolean | Array<keyof F>
    : never;
};

/**
 * Configuration for multiple content types with field intellisense for each
 */
type MultipleContentTypeConfig<T extends readonly EntrySkeletonType[]> = {
  [K in ExtractContentTypeId<T[number]>]?: T[number] extends infer U
    ? U extends EntrySkeletonType<infer F, infer ID>
      ? ID extends K
        ? boolean | Array<keyof F>
        : never
      : never
    : never;
};

/**
 * Type-safe configuration for content types with field intellisense
 * Works with either a single content type or an array of content types
 *
 * @example
 * ```typescript
 * // With a single content type
 * import { TypeBlogPageSkeleton } from '@tuic/contentful/generated/website';
 *
 * const config: ContentTypeConfig<TypeBlogPageSkeleton> = {
 *   blogPage: ['title', 'teaserText'], // Field intellisense here
 *   otherType: ['anyField']            // Other types allowed
 * };
 *
 * // With multiple content types
 * import {
 *   TypeBlogPageSkeleton,
 *   TypeContentCardSkeleton
 * } from '@tuic/contentful/generated/website';
 *
 * const config: ContentTypeConfig<[
 *   TypeBlogPageSkeleton,
 *   TypeContentCardSkeleton
 * ]> = {
 *   blogPage: ['title', 'teaserText'],  // Field intellisense
 *   contentCard: ['title', 'text'],     // Field intellisense
 *   otherType: ['anyField']             // Other types allowed
 * };
 * ```
 */
export type ContentTypeConfig<
  T extends EntrySkeletonType | readonly EntrySkeletonType[],
> = (T extends EntrySkeletonType
  ? SingleContentTypeConfig<T>
  : T extends readonly EntrySkeletonType[]
    ? MultipleContentTypeConfig<T>
    : never) &
  Record<string, boolean | string[]>;

/**
 * Fixes Mein Schiff brand names by replacing spaces with non-breaking spaces to resolve font ligature issues
 * This ensures proper line breaking while maintaining the visual styling and SEO benefits
 * Uses the actual non-breaking space character (U+00A0) instead of HTML entity
 */
export const fixBrandNames = (text: string | undefined): string | undefined => {
  if (!text?.includes('Mein Schiff')) return text;

  const NBSP = '\u00A0'; // Unicode non-breaking space character

  // First handle variants with numbers, Flow, Relax
  let result = text.replace(
    /Mein Schiff (\d|Flow|Relax)/g,
    `Mein${NBSP}Schiff${NBSP}$1`,
  );

  // Handle "Mein Schiff®" case
  result = result.replace(/Mein Schiff®/g, `Mein${NBSP}Schiff®`);

  // Handle standalone "Mein Schiff" case (must come last)
  // Only replace if it's not already part of a processed variant
  result = result.replace(/Mein Schiff(?!\w)/g, `Mein${NBSP}Schiff`);

  return result;
};

/**
 * Apply brand name fix recursively to all string fields in an object
 * Handles special cases like Rich Text fields
 */
export const processContentFields = <T>(obj: T): T => {
  if (!obj) return obj;

  if (typeof obj === 'string') {
    return fixBrandNames(obj) as unknown as T;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => processContentFields(item)) as unknown as T;
  }

  if (typeof obj === 'object') {
    // Create a shallow copy of the object
    const result = { ...obj } as Record<string, unknown>;

    // Special handling for Rich Text fields (Contentful Rich Text format)
    if (
      'nodeType' in result &&
      result.nodeType === 'document' &&
      'content' in result &&
      Array.isArray(result.content)
    ) {
      result.content = processContentFields(result.content);
    } else if (
      'nodeType' in result &&
      result.nodeType === 'text' &&
      'value' in result &&
      typeof result.value === 'string'
    ) {
      // Process text nodes
      result.value = fixBrandNames(result.value);
    } else {
      // Process regular object fields
      Object.keys(result)
        .filter(key => Object.prototype.hasOwnProperty.call(result, key))
        .forEach(key => {
          result[key] = processContentFields(result[key]);
        });
    }

    return result as unknown as T;
  }

  return obj;
};

/**
 * Apply brand name fix to selected fields in an object
 * Only processes the fields specified in the fieldNames array
 */
export const processSelectedContentFields = <T extends Record<string, any>>(
  obj: T,
  fieldNames: string[],
): T => {
  if (!obj) return obj;

  // Create a shallow copy of the object
  const result = { ...obj };

  // Fix: Use type assertion to inform TypeScript about the structure
  return fieldNames.reduce(
    (acc, fieldName) => {
      if (Object.prototype.hasOwnProperty.call(acc, fieldName)) {
        acc[fieldName] = processContentFields(acc[fieldName]);
      }
      return acc;
    },
    result as Record<string, any>,
  ) as T;
};

/**
 * Get the content type ID from a Contentful entry
 */
const getContentTypeId = (
  entry: ContentfulEntry | Record<string, any>,
): string | null => {
  if (entry?.sys?.contentType?.sys?.id) {
    return entry.sys.contentType.sys.id;
  }
  return null;
};

type ContentfulEntry = {
  sys: {
    id?: string;
    type?: string;
    contentType?: {
      sys: {
        id: string;
        type?: string;
        linkType?: string;
      };
    };
    [key: string]: any;
  };
  fields?: Record<string, any>;
  [key: string]: any;
};

/**
 * Process Entry objects to fix brand name text in fields based on configuration
 * Works with any type of Contentful entry
 * Made more flexible to maintain backward compatibility with tests
 *
 * @param entry The Contentful entry to process
 * @param config Optional processing configuration for content types and fields
 */
export const processEntry = <T extends ContentfulEntry | Record<string, any>>(
  entry: T | null,
  config?: BrandNameProcessingConfig,
): T | null => {
  if (!entry) return null;

  // Create a deep copy to avoid modifying original data
  const processedEntry = { ...entry };

  // Process fields by default when no config is provided (for backward compatibility)
  if (config === undefined && processedEntry.fields) {
    processedEntry.fields = processContentFields(processedEntry.fields);
    return processedEntry;
  }

  // Handle when config is a boolean true (process all fields)
  if (config === true) {
    if (processedEntry.fields) {
      processedEntry.fields = processContentFields(processedEntry.fields);
    }
    return processedEntry;
  }

  // Handle when config is a boolean false (skip processing)
  if (config === false) {
    return processedEntry;
  }

  // Handle when config is an object (selective processing)
  if (typeof config === 'object' && config !== null) {
    // Get the content type ID of this entry
    const contentTypeId = getContentTypeId(processedEntry);

    // If no content type ID, return without processing
    if (!contentTypeId) {
      return processedEntry;
    }

    // Get config for this specific content type
    const contentTypeConfig = config[contentTypeId];

    // Process this entry's fields if config exists for this content type
    if (contentTypeConfig !== undefined) {
      // If config is true, process all fields for this content type
      if (contentTypeConfig === true) {
        if (processedEntry.fields) {
          processedEntry.fields = processContentFields(processedEntry.fields);
        }
      }
      // If config is an array of field names, process only those fields
      else if (Array.isArray(contentTypeConfig)) {
        if (processedEntry.fields) {
          processedEntry.fields = processSelectedContentFields(
            processedEntry.fields,
            contentTypeConfig,
          );
        }
      }
    }
    // If no config for this content type, skip processing its own fields
    // but continue to process any nested entries below

    // Process any linked entries in the fields
    if (processedEntry.fields) {
      // Process-linked entries in array fields (like contentBlocks)
      Object.entries(processedEntry.fields).forEach(
        ([fieldName, fieldValue]) => {
          if (fieldValue && typeof fieldValue === 'object') {
            // Check if this is a direct Entry (non-localized structure from withoutUnresolvableLinks)
            if (
              'sys' in fieldValue &&
              (fieldValue as any).sys &&
              (fieldValue as any).sys.type === 'Entry'
            ) {
              (processedEntry.fields as Record<string, any>)[fieldName] =
                processEntry(fieldValue, config);
            }
            // Check if this is an array of Entries (non-localized structure)
            else if (Array.isArray(fieldValue)) {
              (processedEntry.fields as Record<string, any>)[fieldName] =
                fieldValue.map(item => {
                  if (
                    item &&
                    typeof item === 'object' &&
                    item.sys &&
                    item.sys.type === 'Entry'
                  ) {
                    return processEntry(item, config);
                  }
                  return item;
                });
            }
            // Handle localized fields (structure from withAllLocales)
            else {
              Object.entries(fieldValue as Record<string, any>).forEach(
                ([locale, localizedValue]) => {
                  // Process array of linked entries
                  if (Array.isArray(localizedValue)) {
                    (
                      processedEntry.fields as Record<
                        string,
                        Record<string, any>
                      >
                    )[fieldName][locale] = localizedValue.map(item => {
                      if (
                        item &&
                        typeof item === 'object' &&
                        item.sys &&
                        item.sys.type === 'Entry'
                      ) {
                        return processEntry(item, config);
                      }
                      return item;
                    });
                  }
                  // Process a single linked entry
                  else if (
                    localizedValue &&
                    typeof localizedValue === 'object' &&
                    localizedValue.sys &&
                    localizedValue.sys.type === 'Entry'
                  ) {
                    (
                      processedEntry.fields as Record<
                        string,
                        Record<string, any>
                      >
                    )[fieldName][locale] = processEntry(localizedValue, config);
                  }
                },
              );
            }
          }
        },
      );
    }
  }

  return processedEntry;
};

/**
 * Process all entries in a collection
 *
 * @param entries Array of Contentful entries to process
 * @param config Optional processing configuration for content types and fields
 */
export const processEntries = <T extends ContentfulEntry | Record<string, any>>(
  entries: (T | null)[],
  config?: BrandNameProcessingConfig,
): T[] => {
  // If no config is provided, process all entries
  if (!config) {
    return entries
      .map(entry => processEntry(entry))
      .filter((entry): entry is T => entry !== null);
  }

  return entries
    .map(entry => processEntry(entry, config))
    .filter((entry): entry is T => entry !== null);
};

/**
 * Specialized version of processEntries that accepts a skeleton type to provide intellisense
 * for the configuration object based on the entry type.
 *
 * This is particularly useful when used with getEntries where the Skeleton type is already available.
 */
export function processEntriesWithConfig<
  Skeleton extends EntrySkeletonType,
  T extends ContentfulEntry | Record<string, any>,
>(entries: (T | null)[], config: ContentTypeConfig<Skeleton>): T[] {
  return processEntries(entries, config);
}
